/**
 * Sync dynamique des `<meta name="theme-color">` selon le mode courant.
 *
 * Phase 1, incrément 12. Le HTML pose des valeurs initiales (mode
 * classique vert sauge en light, sa version dark en dark). Quand
 * l'utilisateur bascule sur un foyer co-parentage, on met à jour le
 * contenu des deux meta tags via DOM pour que :
 *   - la barre d'URL Android (Chrome) reflète la palette ;
 *   - la status-bar iOS (PWA installée) reste cohérente avec le mode ;
 *   - le splash PWA (Edge / Chrome desktop) suit aussi.
 *
 * Stratégie : on ne CRÉE PAS de nouveaux meta tags — on patche les
 * existants, qui sont les seuls que le navigateur pré-charge. Si les
 * meta tags ne sont pas présents (cas test JSDOM minimal), on no-op.
 */
import type { AppMode } from '../lib/router.js';
import { setStatusBarColor } from '../lib/capacitor/statusBar.js';

interface PaletteEntry {
  light: string;
  dark: string;
}

const PALETTES: Record<AppMode, PaletteEntry> = {
  classic: { light: '#7A9E8E', dark: '#5A7A6C' },
  coparenting: { light: '#5B6C8F', dark: '#424F6C' },
};

/**
 * Met à jour le contenu des deux `<meta name="theme-color">` selon
 * le mode passé. No-op en environnement sans `document` (SSR / tests
 * unit qui n'instancient pas le DOM).
 */
export function syncThemeColor(mode: AppMode): void {
  if (typeof document === 'undefined') return;
  const palette = PALETTES[mode];
  const lightMeta = document.querySelector<HTMLMetaElement>(
    'meta[name="theme-color"][media*="light"]',
  );
  const darkMeta = document.querySelector<HTMLMetaElement>(
    'meta[name="theme-color"][media*="dark"]',
  );
  if (lightMeta) lightMeta.content = palette.light;
  if (darkMeta) darkMeta.content = palette.dark;
  // Pousser aussi vers la status bar Capacitor (no-op en web, async).
  // Phase 1, incrément 20 : on n'attend pas le résultat — la promesse
  // est volontairement ignorée. Si Capacitor StatusBar n'est pas
  // dispo, le wrapper retourne false sans throw.
  void setStatusBarColor(palette.light, 'LIGHT');
}

/** @internal pour les tests : expose la table de couleurs. */
export const _PALETTES = PALETTES;
